export interface DrawLatLng {
  lat: number;
  lng: number;
}

export interface DrawSegmentIdentity {
  startIndex: number;
  endIndex: number;
}

export function isFiniteDrawLatLng(value: unknown): value is DrawLatLng {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<DrawLatLng>;
  return Number.isFinite(candidate.lat) && Number.isFinite(candidate.lng);
}

export function resolveDrawSegmentEndpoints(
  vertices: readonly unknown[],
  segment: DrawSegmentIdentity,
): { start: DrawLatLng; end: DrawLatLng } | null {
  if (!Number.isInteger(segment.startIndex) || !Number.isInteger(segment.endIndex)) return null;
  if (segment.startIndex < 0 || segment.endIndex < 0 || segment.startIndex >= vertices.length || segment.endIndex >= vertices.length) return null;
  if (segment.startIndex === segment.endIndex) return null;

  const start = vertices[segment.startIndex];
  const end = vertices[segment.endIndex];
  if (!isFiniteDrawLatLng(start) || !isFiniteDrawLatLng(end)) return null;
  return { start, end };
}

/**
 * Returns the geometry midpoint for a currently valid draw segment.
 * This is a presentation helper only: callers must not use its result to mutate
 * accepted vertices, draft GeoJSON, history, or persistence during pointer movement.
 */
export function resolveLiveDrawMidpoint(
  vertices: readonly unknown[],
  segment: DrawSegmentIdentity,
): DrawLatLng | null {
  const endpoints = resolveDrawSegmentEndpoints(vertices, segment);
  if (!endpoints) return null;

  return {
    lat: (endpoints.start.lat + endpoints.end.lat) / 2,
    lng: (endpoints.start.lng + endpoints.end.lng) / 2,
  };
}

/** A frame is still safe only when it belongs to the live draw session and marker instance. */
export function isCurrentLiveMidpointFrame(
  frameSessionId: number,
  activeSessionId: number,
  frameMarker: unknown,
  activeMarker: unknown,
): boolean {
  return frameSessionId === activeSessionId && frameMarker === activeMarker;
}
